"use client";

import { useState, createContext, useContext } from "react";
import clsx from "clsx";
import { Image } from "@ui/components/core/Image";
import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import IconLock from "@ui/central-icons/icons/IconLock";
import type { Media } from "@mw/shared/payload-types";
import { isContentGateSubmitted } from "../HubspotForms/shared/utils";

const ContentGateContext = createContext<(() => void) | null>(null);

export const useContentGateSuccess = () => {
  const onSuccess = useContext(ContentGateContext);
  if (!onSuccess) {
    throw new Error("useContentGateSuccess must be used within a ContentGate");
  }
  return onSuccess;
};

export interface ContentGateProps {
  /** Unique identifier for this gate (used for localStorage key) */
  formId: string;
  /** Title to display above the gated content preview */
  title: string;
  /** The gated content to show after form submission */
  children: React.ReactNode;
  /** The form component to show before unlocking */
  form: React.ReactNode;
  /** Optional className for the form wrapper */
  className?: string;
  /** Optional cover image to display alongside the form */
  coverImage?: Media | null;
}

/**
 * Loading skeleton for ContentGate
 * Used as fallback during SSR and dynamic loading
 */
export function ContentGateSkeleton() {
  return <div className='tw-w-full tw-aspect-video tw-bg-gray-200 tw-rounded-2xl tw-animate-pulse' />;
}

/**
 * ContentGate - A component that gates content behind a form
 *
 * Shows a registration form if the user hasn't submitted yet (tracked via localStorage),
 * or shows the gated content if they have.
 */
export function ContentGateClient({ formId, title, children, form, className, coverImage }: ContentGateProps) {
  const [isUnlocked, setIsUnlocked] = useState(() => isContentGateSubmitted(formId));

  const handleSuccess = () => {
    setIsUnlocked(true);
  };

  if (isUnlocked) {
    return <>{children}</>;
  }

  return (
    <div className={clsx("tw-w-full tw-relative tw-bg-white tw-rounded-2xl tw-overflow-hidden", className)}>
      <HairlineCardBorder />
      <div className='tw-flex tw-flex-col lg:tw-flex-row'>
        {/* Left column: Cover image with lock overlay */}
        {coverImage && (
          <div className='tw-relative tw-bg-cream tw-p-6 lg:tw-w-2/5'>
            <div className='tw-relative tw-aspect-video tw-rounded-xl tw-overflow-hidden'>
              <Image resource={coverImage} alt='' fit='cover' className='tw-w-full tw-h-full' />
              {/* Dark overlay */}
              <div className='tw-absolute tw-inset-0 tw-bg-black/40' />
              {/* Lock icon centered */}
              <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'>
                <div className='tw-bg-white/90 tw-rounded-full tw-p-4'>
                  <IconLock className='tw-w-8 tw-h-8 tw-text-gray-700' />
                </div>
              </div>
            </div>
            <h3 className='tw-mt-6 tw-type-2xl tw-text-gray-900'>{title}</h3>
          </div>
        )}
        {/* Right column: Form */}
        <div className='tw-flex tw-flex-1 tw-items-center tw-p-6 lg:tw-p-8'>
          <ContentGateContext.Provider value={handleSuccess}>{form}</ContentGateContext.Provider>
        </div>
      </div>
    </div>
  );
}
